import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createPermissionGroup,
  deletePermissionGroup,
  getPermissionCatalog,
  getPermissionGroups,
  updatePermissionGroup,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import toast from "react-hot-toast";

export const getPermissionGroupsQueryOptions = () => ({
  queryKey: [queryKeys.permissionGroups],
  queryFn: getPermissionGroups,
});

export const getPermissionCatalogQueryOptions = () => ({
  queryKey: [queryKeys.permissionCatalog],
  queryFn: getPermissionCatalog,
});

const useInvalidatePermissionGroups = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      predicate: (query) => !isCanonicalSearchQuery(query),
      queryKey: [queryKeys.permissionGroups],
    });
};

export const useCreatePermissionGroup = () => {
  const invalidatePermissionGroups = useInvalidatePermissionGroups();
  return useMutation({
    mutationFn: createPermissionGroup,
    onSuccess: () => toast.success("Permission group created successfully"),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating permission group");
    },
    onSettled: invalidatePermissionGroups,
  });
};

export const useUpdatePermissionGroup = () => {
  const queryClient = useQueryClient();
  const invalidatePermissionGroups = useInvalidatePermissionGroups();
  return useMutation({
    mutationFn: updatePermissionGroup,
    onSuccess: () => toast.success("Permission group updated successfully"),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating permission group");
    },
    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await invalidatePermissionGroups();
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.users],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.currentUser],
      });
    },
  });
};

export const useDeletePermissionGroup = () => {
  const invalidatePermissionGroups = useInvalidatePermissionGroups();
  return useMutation({
    mutationFn: deletePermissionGroup,
    onSuccess: () => toast.success("Permission group deleted successfully"),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error deleting permission group");
    },
    onSettled: invalidatePermissionGroups,
  });
};
